import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cog, MessageSquare, BarChart3, Database, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { products } from "@/data/mock";

const productIcons = [Cog, MessageSquare, BarChart3, Database];

const categoryColors = {
  "Automation Tools": "cyan",
  "AI Assistants": "teal",
  "Productivity Platforms": "emerald"
};

export default function Products() {
  const availableProducts = products.filter(p => p.status === "available");
  const comingSoonProducts = products.filter(p => p.status === "coming-soon");

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-slate-900" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/80 border border-slate-700 mb-6">
              <Sparkles className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-slate-300">AI-Powered Solutions</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our <span className="text-cyan-400">Products</span>
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              Intelligent tools and platforms built to solve real business challenges. From automation to AI assistants, our products help you work smarter.
            </p>
          </div>
        </div>
      </section>

      {/* Available Products */}
      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-2xl font-bold text-white mb-8">Available Now</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {availableProducts.map((product, index) => {
              const Icon = productIcons[index];
              return (
                <Card key={product.id} className="bg-slate-800/50 border-slate-700 hover:border-cyan-500/50 transition-all duration-300 group overflow-hidden">
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-teal-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-7 h-7 text-cyan-400" />
                      </div>
                      <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                        {product.category}
                      </Badge>
                    </div>
                    <h3 className="text-2xl font-semibold text-white mb-3">{product.name}</h3>
                    <p className="text-slate-400 leading-relaxed mb-6">{product.description}</p>
                    <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white group/btn">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coming Soon Products */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-2xl font-bold text-white mb-8">Coming Soon</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {comingSoonProducts.map((product, index) => {
              const Icon = productIcons[index + 2];
              return (
                <Card key={product.id} className="bg-slate-900/50 border-slate-800 overflow-hidden relative">
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                      Coming Soon
                    </Badge>
                  </div>
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-2xl bg-slate-800 flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-slate-500" />
                    </div>
                    <h3 className="text-2xl font-semibold text-white mb-3">{product.name}</h3>
                    <p className="text-slate-400 leading-relaxed mb-6">{product.description}</p>
                    <Badge variant="outline" className="border-slate-700 text-slate-500">
                      {product.category}
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Custom Solutions CTA */}
      <section className="py-20 bg-slate-900 border-t border-slate-800">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Need a Custom Solution?</h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              We build tailored AI-powered products for businesses with unique challenges. Let's discuss what you need.
            </p>
            <Link to="/consultation">
              <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold px-8 group">
                Discuss Your Needs
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
